import * as THREE from 'three';
import { Axial, roundAxial, worldToAxial } from '../map/HexCoordinates';

/**
 * Turns the mouse position into the hex under it.
 *
 * Both the terrain and the water surface are cast against. Water is opaque, so
 * the nearest hit is whatever the player can actually see — and it has to be:
 * letting the ray pass through the surface to the seabed would land it up to
 * three cells away from the cursor, because at this camera pitch two units of
 * depth drag the hit point almost three units sideways.
 *
 * Only the horizontal part of the hit matters. Height plays no role in deciding
 * which cell was struck, so the terrain's relief needs no special handling.
 */
export class HexPicker {
  private readonly domElement: HTMLElement;
  private readonly camera: THREE.OrthographicCamera;
  private readonly targets: THREE.Object3D[];

  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private pointerInside = false;

  /**
   * Gdzie dokładnie promień trafił w teren, albo `null` przy pudle.
   *
   * Pole, a nie druga metoda z drugim promieniem: to jest ten sam raycast, tyle
   * że bez zaokrąglenia do pola. Potrzebne, odkąd na jednym polu stoi sześcioro
   * — samo pole nie mówi wtedy, o kogo chodzi.
   */
  lastPoint: THREE.Vector3 | null = null;

  /** Milliseconds spent in the last pick, for the stats readout. */
  lastPickMs = 0;

  constructor(
    domElement: HTMLElement,
    camera: THREE.OrthographicCamera,
    targets: THREE.Object3D[]
  ) {
    this.domElement = domElement;
    this.camera = camera;
    this.targets = targets;

    this.domElement.addEventListener('mousemove', this.onMouseMove);
    this.domElement.addEventListener('mouseleave', this.onMouseLeave);
  }

  private onMouseMove = (event: MouseEvent): void => {
    // Against the element's own box, so the CSS upscaling from 480p to the
    // window does not throw the coordinates off.
    //
    // The image fills the padding box, not the box getBoundingClientRect()
    // reports: the canvas carries a CSS border and box-sizing: border-box puts
    // it inside the declared size. clientLeft/clientTop are the border widths
    // and clientWidth/clientHeight the box within them, so this stays right
    // whatever border the canvas is given.
    const bounds = this.domElement.getBoundingClientRect();
    const x = event.clientX - bounds.left - this.domElement.clientLeft;
    const y = event.clientY - bounds.top - this.domElement.clientTop;

    this.pointer.x = (x / this.domElement.clientWidth) * 2 - 1;
    this.pointer.y = -(y / this.domElement.clientHeight) * 2 + 1;
    this.pointerInside = true;
  };

  private onMouseLeave = (): void => {
    this.pointerInside = false;
  };

  /** The cell under the cursor, or null when the cursor is off the map. */
  pick(): Axial | null {
    if (!this.pointerInside) return null;

    const start = performance.now();

    this.raycaster.setFromCamera(this.pointer, this.camera);

    // For an orthographic camera Three.js starts the ray in the plane of the
    // camera itself, and CAMERA_NEAR is negative precisely so that part of the
    // visible scene sits behind that plane. Ray.intersectTriangle rejects a
    // negative t outright, and the bounding-sphere test before it drops whole
    // chunks the same way, so that part of the view would answer nothing.
    // Pulling the origin back onto the near plane puts the whole depth range in
    // front of it. Measured: without this, at zoom 0.5 one screen point in seven
    // is unreachable, and the dead band reaches a fifth of the way up the frame.
    this.raycaster.ray.origin.addScaledVector(
      this.raycaster.ray.direction,
      this.camera.near
    );

    const hits = this.raycaster.intersectObjects(this.targets, false);

    this.lastPickMs = performance.now() - start;
    if (hits.length === 0) {
      this.lastPoint = null;
      return null;
    }

    this.lastPoint = hits[0].point.clone();
    const { x, z } = hits[0].point;
    const fractional = worldToAxial(x, z);
    return roundAxial(fractional.q, fractional.r);
  }

  dispose(): void {
    this.domElement.removeEventListener('mousemove', this.onMouseMove);
    this.domElement.removeEventListener('mouseleave', this.onMouseLeave);
  }
}
